import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Plus,
  Edit2,
  Trash2,
  Star,
  X,
  Settings,
  GitBranch,
  Zap,
  Brain,
  Clock,
  Users,
  ArrowLeft,
} from 'lucide-react';
import { Card, Spinner, ConfirmDialog, useToast } from '@centraoneplus/ui';
import {
  CARD,
  CARD_CLASS,
  CardFoot,
  Checkbox,
  ColumnHeader,
  EmptyState,
  FOCUS_RING,
  IconButton,
  Input,
  Notice,
  StatusChip,
  Textarea,
  Button as KitButton,
} from '@shared/ui/kit';
import { SettingsCard, SectionHead, FieldLabel } from './chrome';
import { pipelineService } from '@/services/pipeline.service';
import type { Pipeline } from '@/types/pipeline';
import { GeneralTab, StagesTab, FieldsTab, WorkflowTab, AITab, SLATab, RoutingTab } from './pipeline-config';

// Tab configuration
type TabId = 'general' | 'stages' | 'fields' | 'workflows' | 'ai' | 'sla' | 'routing';

interface Tab {
  id: TabId;
  label: string;
  icon: React.ElementType;
  description: string;
}

const TABS: Tab[] = [
  { id: 'general', label: 'General', icon: Settings, description: 'Basic pipeline settings' },
  { id: 'stages', label: 'Stages', icon: GitBranch, description: 'Workflow stages' },
  // Fields tab hidden — custom fields are defined in Settings → Field Definitions
  // (the live source for ticket forms). The render branch + FieldsTab import are
  // kept (unreachable) for easy revert.
  // { id: 'fields', label: 'Fields', icon: ListFilter, description: 'Custom field schema' },
  // Workflows tab hidden — automation rules editor is incomplete (conditions/actions
  // not yet implemented). The render branch + WorkflowTab import are kept (unreachable)
  // for easy revert.
  // { id: 'workflows', label: 'Workflows', icon: Zap, description: 'Automation rules' },
  { id: 'ai', label: 'AI', icon: Brain, description: 'AI configuration' },
  { id: 'sla', label: 'SLA', icon: Clock, description: 'Service level targets' },
  { id: 'routing', label: 'Routing', icon: Users, description: 'Assignment rules' },
];

interface PipelineFormData {
  name: string;
  description: string;
  isDefault: boolean;
}

export default function PipelineSettings() {
  const toast = useToast();
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pipeline pending deletion — drives the confirmation dialog.
  const [deleteTarget, setDeleteTarget] = useState<Pipeline | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Selected pipeline for detailed config
  const [selectedPipelineId, setSelectedPipelineId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabId>('general');

  // Pipeline form state for creating new
  const [isCreatingPipeline, setIsCreatingPipeline] = useState(false);
  const [pipelineForm, setPipelineForm] = useState<PipelineFormData>({
    name: '',
    description: '',
    isDefault: false,
  });
  const [isSaving, setIsSaving] = useState(false);

  const selectedPipeline = pipelines.find((p) => p.id === selectedPipelineId);

  const fetchPipelines = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await pipelineService.getPipelines();
      setPipelines(data);
    } catch (err) {
      setError('Failed to load pipelines');
      console.error('Error fetching pipelines:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPipelines();
  }, [fetchPipelines]);

  // `?create=1` (the Tickets page's "Create your first pipeline") opens the
  // new-pipeline form straight away. The param is dropped so a refresh or
  // Back does not reopen it.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get('create') !== '1') return;
    setIsCreatingPipeline(true);
    const next = new URLSearchParams(searchParams);
    next.delete('create');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleCreatePipeline = async () => {
    if (!pipelineForm.name.trim()) return;

    try {
      setIsSaving(true);
      const newPipeline = await pipelineService.createPipeline({
        name: pipelineForm.name,
        description: pipelineForm.description || undefined,
        isDefault: pipelineForm.isDefault,
      });
      // If the new pipeline is the default, the backend just cleared the
      // previous default — mirror that locally so we don't show two defaults.
      // (newPipeline.isDefault also covers the "first pipeline is forced
      // default" case even when the checkbox was left unchecked.)
      setPipelines((prev) => {
        const cleared = newPipeline.isDefault
          ? prev.map((p) => ({ ...p, isDefault: false }))
          : prev;
        return [...cleared, newPipeline];
      });
      setIsCreatingPipeline(false);
      setPipelineForm({ name: '', description: '', isDefault: false });
      // Immediately select the new pipeline
      setSelectedPipelineId(newPipeline.id);
      toast.success('Pipeline created');
    } catch (err) {
      console.error('Error creating pipeline:', err);
      setError('Failed to create pipeline');
      toast.error(
        'Failed to create pipeline',
        (err as any)?.response?.data?.message ||
          (err as any)?.response?.data?.error?.message ||
          (err as any)?.message ||
          'Please try again.'
      );
    } finally {
      setIsSaving(false);
    }
  };

  // Validate, then open the confirmation dialog. Blocking reasons (default
  // pipeline / has tickets) surface as an error toast instead of the dialog.
  const requestDeletePipeline = (pipeline: Pipeline) => {
    if (pipeline.isDefault) {
      toast.error('Cannot delete pipeline', 'This is the default pipeline. Set another pipeline as default first.');
      return;
    }

    const count = pipeline.ticketCount ?? 0;
    if (count > 0) {
      toast.error(
        'Cannot delete pipeline',
        `"${pipeline.name}" has ${count} ticket${count === 1 ? '' : 's'}. Move or reassign them first.`,
      );
      return;
    }

    setDeleteTarget(pipeline);
  };

  const confirmDeletePipeline = async () => {
    if (!deleteTarget) return;
    const { id, name } = deleteTarget;

    try {
      setIsDeleting(true);
      await pipelineService.deletePipeline(id);
      setPipelines((prev) => prev.filter((p) => p.id !== id));
      if (selectedPipelineId === id) {
        setSelectedPipelineId(null);
      }
      toast.success('Pipeline deleted', `"${name}" was deleted.`);
      setDeleteTarget(null);
    } catch (err: any) {
      console.error('Error deleting pipeline:', err);
      const message = err?.response?.data?.message || 'Failed to delete pipeline.';
      toast.error('Delete failed', message);
    } finally {
      setIsDeleting(false);
    }
  };

  const handlePipelineUpdate = (updatedPipeline: Pipeline) => {
    setPipelines(pipelines.map((p) => (p.id === updatedPipeline.id ? updatedPipeline : p)));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  // Show pipeline detail view when one is selected
  if (selectedPipeline) {
    return (
      <div className="flex flex-col gap-[14px]">
        {/* Header with back button */}
        <SettingsCard>
          <div className="flex items-start gap-[14px]">
            <IconButton
              label="Back to all pipelines"
              onClick={() => setSelectedPipelineId(null)}
            >
              <ArrowLeft className="h-[15px] w-[15px]" aria-hidden="true" />
            </IconButton>
            <div className="min-w-0 flex-1">
              <div className="flex min-h-[34px] items-center gap-[10px]">
                <h2
                  className="truncate text-[18px] font-bold leading-[1.15] tracking-[-.018em] text-[var(--co-ink)]"
                  style={{ fontFamily: 'var(--co-font-display)' }}
                >
                  {selectedPipeline.name}
                </h2>
                {selectedPipeline.isDefault && (
                  <StatusChip tone="ok" title="New tickets land in this pipeline unless something says otherwise">
                    <Star className="h-[10px] w-[10px]" aria-hidden="true" />
                    Default
                  </StatusChip>
                )}
                {!selectedPipeline.isActive && <StatusChip tone="neutral">Inactive</StatusChip>}
              </div>
              {selectedPipeline.description && (
                <p className="mt-[5px] max-w-[84ch] text-[12.5px] leading-[1.55] text-[var(--co-ink-3)]">
                  {selectedPipeline.description}
                </p>
              )}
            </div>
          </div>
        </SettingsCard>

        {error && (
          <div className="flex items-start gap-3">
            <div className="min-w-0 flex-1">
              <Notice tone="danger" headline="Something went wrong" body={error} />
            </div>
            <IconButton label="Dismiss error" onClick={() => setError(null)}>
              <X className="h-[15px] w-[15px]" aria-hidden="true" />
            </IconButton>
          </div>
        )}

        {/* Tab Navigation. Five sections is past the point where a segmented
            control reads as a choice, so this stays a tab strip — restyled, but
            still the same five tabs switching the same five panels. */}
        <div className={`overflow-hidden ${CARD_CLASS}`} style={CARD}>
          <nav
            className="flex gap-6 border-b border-[var(--co-line-subtle)] px-5"
            aria-label="Pipeline configuration sections"
          >
            {TABS.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  title={tab.description}
                  onClick={() => setActiveTab(tab.id)}
                  className={`co-mono -mb-px inline-flex items-center gap-2 border-b-2 py-[14px] text-[10.5px] uppercase tracking-[.12em] transition-colors ${FOCUS_RING}`}
                  style={{
                    borderBottomColor: isActive ? 'var(--co-brass)' : 'transparent',
                    color: isActive ? 'var(--co-ink)' : 'var(--co-ink-3)',
                    fontWeight: isActive ? 600 : 500,
                  }}
                >
                  <Icon
                    className="h-[15px] w-[15px]"
                    style={{ color: isActive ? 'var(--co-brass-ink)' : 'var(--co-ink-4)' }}
                    aria-hidden="true"
                  />
                  {tab.label}
                </button>
              );
            })}
          </nav>

          {/* Tab Content */}
          <div className="min-h-[400px] p-5">
            {activeTab === 'general' && (
              <GeneralTab
                pipeline={selectedPipeline}
                onUpdate={handlePipelineUpdate}
                onRefresh={fetchPipelines}
              />
            )}
            {activeTab === 'stages' && (
              <StagesTab pipeline={selectedPipeline} onUpdate={handlePipelineUpdate} />
            )}
            {activeTab === 'fields' && (
              <FieldsTab pipeline={selectedPipeline} onUpdate={handlePipelineUpdate} />
            )}
            {activeTab === 'workflows' && (
              <WorkflowTab pipeline={selectedPipeline} onUpdate={handlePipelineUpdate} />
            )}
            {activeTab === 'ai' && (
              <AITab pipeline={selectedPipeline} onUpdate={handlePipelineUpdate} />
            )}
            {activeTab === 'sla' && (
              <SLATab pipeline={selectedPipeline} onUpdate={handlePipelineUpdate} />
            )}
            {activeTab === 'routing' && (
              <RoutingTab pipeline={selectedPipeline} onUpdate={handlePipelineUpdate} />
            )}
          </div>
        </div>
      </div>
    );
  }

  // Show pipeline list view
  return (
    <div className="flex flex-col gap-[14px]">
      <SettingsCard>
        <SectionHead
          title="Pipelines & stages"
          description="A pipeline is the route a ticket takes; its stages are the steps along it. Changing a stage here does not move any ticket that is already in one."
          actions={
            <KitButton
              variant="primary"
              onClick={() => setIsCreatingPipeline(true)}
              disabled={isCreatingPipeline}
            >
              <Plus className="h-[15px] w-[15px]" aria-hidden="true" />
              New pipeline
            </KitButton>
          }
        />
      </SettingsCard>

      {error && (
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <Notice tone="danger" headline="Something went wrong" body={error} />
          </div>
          <IconButton label="Dismiss error" onClick={() => setError(null)}>
            <X className="h-[15px] w-[15px]" aria-hidden="true" />
          </IconButton>
        </div>
      )}

      {/* Create Pipeline Form */}
      {isCreatingPipeline && (
        <SettingsCard style={{ borderColor: 'var(--co-brass-line)', background: 'var(--co-brass-wash)' }}>
          <SectionHead title="New pipeline" />
          <div className="mt-4 space-y-4">
            <div>
              <FieldLabel htmlFor="pipeline-name">Pipeline name *</FieldLabel>
              <div className="mt-[7px]">
                <Input
                  id="pipeline-name"
                  type="text"
                  value={pipelineForm.name}
                  onChange={(e) => setPipelineForm({ ...pipelineForm, name: e.target.value })}
                  placeholder="e.g., Sales Pipeline, Support Pipeline"
                />
              </div>
            </div>
            <div>
              <FieldLabel htmlFor="pipeline-description">Description</FieldLabel>
              <div className="mt-[7px]">
                <Textarea
                  id="pipeline-description"
                  value={pipelineForm.description}
                  onChange={(e) =>
                    setPipelineForm({ ...pipelineForm, description: e.target.value })
                  }
                  placeholder="Describe the purpose of this pipeline…"
                  rows={2}
                />
              </div>
            </div>
            <div className="flex items-center gap-[10px]">
              <Checkbox
                label="Set as default pipeline for new tickets"
                checked={pipelineForm.isDefault}
                onChange={(next) => setPipelineForm({ ...pipelineForm, isDefault: next })}
              />
              <span className="text-[12.5px] text-[var(--co-ink-2)]">
                Set as default pipeline for new tickets
              </span>
            </div>
            <div className="flex justify-end gap-2">
              <KitButton
                onClick={() => {
                  setIsCreatingPipeline(false);
                  setPipelineForm({ name: '', description: '', isDefault: false });
                }}
              >
                Cancel
              </KitButton>
              <KitButton
                variant="primary"
                onClick={handleCreatePipeline}
                disabled={isSaving || !pipelineForm.name.trim()}
                disabledReason={!pipelineForm.name.trim() ? 'Name required' : undefined}
              >
                {isSaving ? 'Creating…' : 'Create pipeline'}
              </KitButton>
            </div>
          </div>
        </SettingsCard>
      )}

      {/* Pipeline List */}
      <div className={`overflow-hidden ${CARD_CLASS}`} style={CARD}>
        <ColumnHeader
          columns={[
            { id: 'pipeline', label: 'Pipeline' },
            { id: 'stages', label: 'Stages', width: 110, align: 'right' },
            { id: 'tickets', label: 'Tickets', width: 110, align: 'right' },
            { id: 'actions', label: '', width: 96 },
          ]}
        />

        {pipelines.length === 0 ? (
          <EmptyState
            title="No pipelines configured yet"
            body="Every ticket needs a route. Create one pipeline and its five default stages are seeded with it."
            action={
              <KitButton variant="primary" onClick={() => setIsCreatingPipeline(true)}>
                <Plus className="h-[15px] w-[15px]" aria-hidden="true" />
                Create your first pipeline
              </KitButton>
            }
          />
        ) : (
          pipelines.map((pipeline) => (
            <div
              key={pipeline.id}
              role="row"
              onClick={() => setSelectedPipelineId(pipeline.id)}
              className="flex cursor-pointer items-center gap-3 border-b border-l-2 border-[var(--co-line-subtle)] px-[14px] py-[13px] transition-colors last:border-b-0 hover:bg-[var(--co-selected)]"
              style={{
                borderLeftColor: 'transparent',
                background: pipeline.isActive === false ? 'var(--co-sunken)' : undefined,
              }}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-[10px]">
                  <span className="truncate text-[13.5px] font-semibold text-[var(--co-ink)]">
                    {pipeline.name}
                  </span>
                  {pipeline.isDefault && (
                    <StatusChip tone="ok">
                      <Star className="h-[10px] w-[10px]" aria-hidden="true" />
                      Default
                    </StatusChip>
                  )}
                  {!pipeline.isActive && <StatusChip tone="neutral">Inactive</StatusChip>}
                </div>
                {pipeline.description && (
                  <p className="mt-[4px] line-clamp-2 text-[12px] text-[var(--co-ink-3)]">
                    {pipeline.description}
                  </p>
                )}
              </div>

              <div
                className="co-mono flex-none text-right text-[13px] font-semibold tabular-nums text-[var(--co-ink)]"
                style={{ width: 110 }}
              >
                {pipeline.stages?.length || 0}
              </div>

              {/* A pipeline whose ticket count the server did not send renders
                  an em dash, never a zero — "no tickets" and "not counted" are
                  different answers and only one of them is good news. */}
              <div
                className="co-mono flex-none text-right text-[13px] font-semibold tabular-nums"
                style={{
                  width: 110,
                  color:
                    pipeline.ticketCount === undefined
                      ? 'var(--co-ink-4)'
                      : 'var(--co-ink)',
                }}
              >
                {pipeline.ticketCount === undefined ? '—' : pipeline.ticketCount}
              </div>

              {/* Start-aligned (padded to the 96px cell) so Configure stays in
                  one column on the default row, which has no Delete. */}
              <div
                className="flex flex-none justify-start gap-2 pl-5"
                style={{ width: 96 }}
                onClick={(e) => e.stopPropagation()}
              >
                <IconButton
                  label={`Configure ${pipeline.name}`}
                  onClick={() => setSelectedPipelineId(pipeline.id)}
                >
                  <Edit2 className="h-[15px] w-[15px]" aria-hidden="true" />
                </IconButton>
                {!pipeline.isDefault && (
                  <IconButton
                    label={`Delete ${pipeline.name}`}
                    onClick={() => requestDeletePipeline(pipeline)}
                    className="text-[var(--co-burgundy)] hover:border-[var(--co-burgundy)]"
                  >
                    <Trash2 className="h-[15px] w-[15px]" aria-hidden="true" />
                  </IconButton>
                )}
              </div>
            </div>
          ))
        )}

        {pipelines.length > 0 && (
          <CardFoot
            left={`${pipelines.length} pipeline${pipelines.length === 1 ? '' : 's'}`}
            note="The default pipeline cannot be deleted — something has to catch a ticket that names no pipeline."
          />
        )}
      </div>

      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => {
          if (!isDeleting) setDeleteTarget(null);
        }}
        onConfirm={confirmDeletePipeline}
        title="Delete pipeline"
        message={
          deleteTarget
            ? `Delete pipeline "${deleteTarget.name}"? This will also remove all its stages. This action cannot be undone.`
            : ''
        }
        confirmLabel="Delete"
        variant="danger"
        loading={isDeleting}
      />
    </div>
  );
}

// Placeholder for upcoming features
function ComingSoonTab({ title, description }: { title: string; description: string }) {
  return (
    <Card className="p-12 text-center">
      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-100 mb-4 dark:bg-gray-800">
        <Zap className="h-6 w-6 text-gray-400 dark:text-gray-500" />
      </div>
      <h3 className="text-lg font-medium text-gray-900 mb-2 dark:text-gray-100">{title}</h3>
      <p className="text-sm text-gray-500 max-w-md mx-auto dark:text-gray-400">{description}</p>
      <p className="text-xs text-gray-400 mt-4 dark:text-gray-500">Coming soon</p>
    </Card>
  );
}
